import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, Upload, Download, Plus, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RESPONSE_TYPES = {
  NUMBER: "Numero", TEXT: "Testo", YES_NO: "Sì/No", MULTI_CHOICE: "Scelta multipla", DATE: "Data", ATTACHMENT: "Allegato",
};
const STATUSES = ["ESEMPIO – DA VERIFICARE", "UFFICIALE", "BOZZA"];
const STATUS_STYLE = {
  "ESEMPIO – DA VERIFICARE": "bg-amber-50 text-amber-900 border-amber-300",
  UFFICIALE: "bg-green-50 text-green-800 border-green-300",
  BOZZA: "bg-slate-100 text-slate-700 border-slate-300",
};
const EMPTY = {
  datapoint_code: "", section_code: "S1", priority: 1, official_indicator_number: "", title: "", description: "",
  response_type: "TEXT", unit: "", bank_reference: "", vsme_codes: "", guidance: "", evidence_required: false,
  catalog_status: "BOZZA", active: true,
};

function IndicatorDialog({ open, onClose, initial, sections, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...EMPTY, ...initial, vsme_codes: (initial.vsme_codes || []).join("; "),
      official_indicator_number: initial.official_indicator_number ?? "", unit: initial.unit || "" } : EMPTY);
  }, [open, initial]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        priority: Number(form.priority),
        official_indicator_number: form.official_indicator_number === "" ? null : Number(form.official_indicator_number),
        unit: form.unit || null,
        vsme_codes: form.vsme_codes.split(/[;,]/).map((s) => s.trim()).filter(Boolean),
      };
      if (payload.official_indicator_number === null) delete payload.official_indicator_number;
      if (initial?.id) {
        delete payload.datapoint_code;
        await api.put(`/admin/mef/indicators/${initial.id}`, payload);
      } else {
        await api.post("/admin/mef/indicators", payload);
      }
      toast.success(initial?.id ? "Indicatore aggiornato" : "Indicatore creato");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="indicator-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? `Modifica ${initial.datapoint_code}` : "Nuovo indicatore"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <label className="space-y-1"><span className="font-semibold">Codice MEF *</span>
            <Input value={form.datapoint_code} disabled={!!initial?.id} onChange={(e) => set("datapoint_code", e.target.value.toUpperCase())} data-testid="indicator-code" placeholder="MEF.41" /></label>
          <label className="space-y-1"><span className="font-semibold">N. nel documento</span>
            <Input type="number" value={form.official_indicator_number} onChange={(e) => set("official_indicator_number", e.target.value)} data-testid="indicator-number" /></label>
          <label className="space-y-1"><span className="font-semibold">Sezione *</span>
            <Select value={form.section_code} onValueChange={(v) => set("section_code", v)}>
              <SelectTrigger data-testid="indicator-section"><SelectValue /></SelectTrigger>
              <SelectContent>{sections.map((s) => <SelectItem key={s.code} value={s.code}>{s.order}. {s.title}</SelectItem>)}</SelectContent>
            </Select></label>
          <label className="space-y-1"><span className="font-semibold">Priorità *</span>
            <Select value={String(form.priority)} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger data-testid="indicator-priority"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="1">1 – Essenziale</SelectItem><SelectItem value="2">2 – Aggiuntiva</SelectItem></SelectContent>
            </Select></label>
          <label className="space-y-1 sm:col-span-2"><span className="font-semibold">Titolo *</span>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="indicator-title" /></label>
          <label className="space-y-1 sm:col-span-2"><span className="font-semibold">Descrizione</span>
            <Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="indicator-description" /></label>
          <label className="space-y-1"><span className="font-semibold">Tipo risposta *</span>
            <Select value={form.response_type} onValueChange={(v) => set("response_type", v)}>
              <SelectTrigger data-testid="indicator-response-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(RESPONSE_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select></label>
          <label className="space-y-1"><span className="font-semibold">Unità di misura</span>
            <Input value={form.unit} onChange={(e) => set("unit", e.target.value)} data-testid="indicator-unit" placeholder="tCO2e, kWh, %…" /></label>
          <label className="space-y-1"><span className="font-semibold">Riferimento normativo banca</span>
            <Input value={form.bank_reference} onChange={(e) => set("bank_reference", e.target.value)} data-testid="indicator-bank-reference" placeholder="es. Pillar 3 – Template 1" /></label>
          <label className="space-y-1"><span className="font-semibold">Codici VSME (separati da ;)</span>
            <Input value={form.vsme_codes} onChange={(e) => set("vsme_codes", e.target.value)} data-testid="indicator-vsme" placeholder="VSME.B3.SCOPE1" /></label>
          <label className="space-y-1 sm:col-span-2"><span className="font-semibold">Note guida</span>
            <Textarea rows={2} value={form.guidance} onChange={(e) => set("guidance", e.target.value)} data-testid="indicator-guidance" /></label>
          <label className="space-y-1"><span className="font-semibold">Stato catalogo</span>
            <Select value={form.catalog_status} onValueChange={(v) => set("catalog_status", v)}>
              <SelectTrigger data-testid="indicator-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select></label>
          <div className="flex flex-col gap-2 justify-end">
            <label className="flex items-center gap-2"><Switch checked={form.evidence_required} onCheckedChange={(v) => set("evidence_required", v)} data-testid="indicator-evidence" /> Evidenza documentale obbligatoria</label>
            {initial?.id && <label className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="indicator-active" /> Attivo</label>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy} className="bg-green-700 hover:bg-green-800 text-white" data-testid="indicator-save">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, onClose, onImported }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [deactivate, setDeactivate] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setPreview(null); }, [open]);
  const run = async (dryRun) => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Seleziona un file .xlsx o .csv"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/mef/indicators/import?dry_run=${dryRun}&deactivate_missing=${deactivate}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (!dryRun && data.applied) { toast.success(`Import completato: ${data.create} creati, ${data.update} aggiornati`); onImported(); }
      if (data.errors > 0) toast.error(`${data.errors} righe con errori: correggi il file`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Importa catalogo da Excel/CSV</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Upsert per codice: le righe con codice esistente aggiornano l'indicatore, le altre lo creano. Scarica il modello per le colonne attese.</p>
        <input type="file" ref={fileRef} accept=".xlsx,.csv" className="text-sm" data-testid="import-file" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={deactivate} onCheckedChange={setDeactivate} data-testid="import-deactivate" /> Disattiva gli indicatori non presenti nel file</label>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => run(true)} disabled={busy} data-testid="import-preview">Anteprima</Button>
          <Button onClick={() => run(false)} disabled={busy || !preview || preview.errors > 0} className="bg-green-700 hover:bg-green-800 text-white" data-testid="import-apply">Importa</Button>
        </div>
        {preview && (
          <div className="space-y-2 text-sm" data-testid="import-report">
            <p><strong data-testid="import-total">{preview.total}</strong> righe · <span data-testid="import-create">{preview.create}</span> da creare · <span data-testid="import-update">{preview.update}</span> da aggiornare · <span className="text-red-700" data-testid="import-errors">{preview.errors}</span> errori{preview.applied ? " · APPLICATO" : ""}</p>
            {preview.missing_in_file?.length > 0 && <p className="text-xs text-slate-600">Non presenti nel file: {preview.missing_in_file.join(", ")}</p>}
            <div className="max-h-64 overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50"><tr><th className="p-1 text-left">Riga</th><th className="p-1 text-left">Codice</th><th className="p-1 text-left">Titolo</th><th className="p-1 text-left">Azione</th><th className="p-1 text-left">Errori</th></tr></thead>
                <tbody>{preview.rows.map((r) => (
                  <tr key={r.line} className={r.errors.length ? "bg-red-50" : ""} data-testid={`import-row-${r.line}`}>
                    <td className="p-1">{r.line}</td><td className="p-1 font-mono">{r.datapoint_code}</td><td className="p-1">{r.title}</td>
                    <td className="p-1">{r.action}</td><td className="p-1 text-red-700">{r.errors.join("; ")}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminMefIndicators() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [section, setSection] = useState("ALL");
  const [priority, setPriority] = useState("ALL");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/mef/indicators?include_inactive=true");
      setData(d); setError(null);
    } catch (e) { setError(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => (data?.items || []).filter((i) =>
    (section === "ALL" || i.section_code === section)
    && (priority === "ALL" || String(i.priority) === priority)
    && (!search || `${i.datapoint_code} ${i.title}`.toLowerCase().includes(search.toLowerCase()))), [data, section, priority, search]);

  const downloadTemplate = async () => {
    const res = await api.get("/admin/mef/indicators/template.xlsx", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "catalogo_indicatori_mef.xlsx"; a.click(); URL.revokeObjectURL(url);
  };

  if (error) return <p className="text-sm text-red-700" data-testid="mef-catalog-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600">Caricamento…</p>;

  return (
    <div className="space-y-6" data-testid="mef-catalog-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3"><Landmark className="w-7 h-7 text-green-700" aria-hidden="true" /> Catalogo indicatori PMI–Banche</h1>
          <p className="text-sm text-slate-600 mt-1">Documento per il dialogo di sostenibilità tra PMI e Banche (MEF, dic. 2024 – agg. dic. 2025). Strumento volontario di supporto. <Link to="/admin/frameworks" className="underline">Mappatura VSME</Link></p>
        </div>
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template"><Download className="w-4 h-4 mr-1" aria-hidden="true" /> Modello Excel</Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="open-import"><Upload className="w-4 h-4 mr-1" aria-hidden="true" /> Importa</Button>
          <Button onClick={() => setEditing({})} className="bg-green-700 hover:bg-green-800 text-white" data-testid="new-indicator"><Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Nuovo</Button>
        </div>
      </header>

      {data.stats.to_verify > 0 && (
        <div className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950" data-testid="catalog-example-warning">
          <AlertTriangle className="w-5 h-5 shrink-0" aria-hidden="true" />
          <span><strong>{data.stats.to_verify}</strong> indicatori sono marcati "ESEMPIO – DA VERIFICARE": non sono la lista ufficiale. Importa il catalogo ufficiale da Excel/CSV per sostituirli.</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        {[["Totale", data.stats.total, "stat-total"], ["Priorità 1", data.stats.priority_1, "stat-p1"], ["Priorità 2", data.stats.priority_2, "stat-p2"], ["Ufficiali", data.stats.official, "stat-official"], ["Da verificare", data.stats.to_verify, "stat-to-verify"]].map(([l, v, id]) => (
          <Card key={id}><CardContent className="p-3"><p className="text-xs text-slate-500">{l}</p><p className="text-xl font-bold" data-testid={id}>{v}</p></CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader className="flex flex-col md:flex-row md:items-center gap-2">
          <CardTitle className="text-base">Indicatori ({items.length})</CardTitle>
          <div className="flex flex-wrap gap-2 md:ml-auto">
            <Input placeholder="Cerca codice o titolo" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-56" data-testid="filter-search" />
            <Select value={section} onValueChange={setSection}><SelectTrigger className="h-9 w-56" data-testid="filter-section"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Tutte le sezioni</SelectItem>{data.sections.map((s) => <SelectItem key={s.code} value={s.code}>{s.order}. {s.title}</SelectItem>)}</SelectContent></Select>
            <Select value={priority} onValueChange={setPriority}><SelectTrigger className="h-9 w-40" data-testid="filter-priority"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Tutte le priorità</SelectItem><SelectItem value="1">Priorità 1</SelectItem><SelectItem value="2">Priorità 2</SelectItem></SelectContent></Select>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="indicators-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr><th className="p-2 text-left">N.</th><th className="p-2 text-left">Codice</th><th className="p-2 text-left">Sez.</th><th className="p-2 text-left">Pr.</th><th className="p-2 text-left">Titolo</th><th className="p-2 text-left">Tipo</th><th className="p-2 text-left">VSME</th><th className="p-2 text-left">Rif. banca</th><th className="p-2 text-left">Evid.</th><th className="p-2 text-left">Stato</th><th className="p-2" /></tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className={`border-t ${i.active ? "" : "opacity-50"}`} data-testid={`indicator-row-${i.datapoint_code}`}>
                  <td className="p-2">{i.official_indicator_number ?? "—"}</td>
                  <td className="p-2 font-mono text-xs">{i.datapoint_code}</td>
                  <td className="p-2">{i.section_code}</td>
                  <td className="p-2"><Badge variant="outline" className={i.priority === 1 ? "border-green-600 text-green-800" : "border-slate-400"}>P{i.priority}</Badge></td>
                  <td className="p-2 max-w-md"><p className="font-medium">{i.title}</p>{i.guidance && <p className="text-xs text-slate-500 line-clamp-1">{i.guidance}</p>}</td>
                  <td className="p-2 text-xs">{RESPONSE_TYPES[i.response_type] || i.response_type}{i.unit ? ` (${i.unit})` : ""}</td>
                  <td className="p-2 text-xs font-mono">{i.vsme_codes.join(", ") || "—"}</td>
                  <td className="p-2 text-xs">{i.bank_reference || "—"}</td>
                  <td className="p-2 text-xs">{i.evidence_required ? "Sì" : "—"}</td>
                  <td className="p-2"><Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[i.catalog_status] || ""}`} data-testid={`indicator-status-${i.datapoint_code}`}>{i.catalog_status}</Badge></td>
                  <td className="p-2"><Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid={`edit-${i.datapoint_code}`}><Pencil className="w-4 h-4" aria-hidden="true" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="text-xs text-slate-500" data-testid="mef-disclaimer">{data.disclaimer}</p>

      <IndicatorDialog open={editing !== null} onClose={() => setEditing(null)} initial={editing?.id ? editing : null} sections={data.sections} onSaved={load} />
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
    </div>
  );
}
